import { useEffect, useState } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  MapPin,
  ShieldCheck,
  Sparkles,
  Hammer,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";

const HERO_IMG = "/images/screens/screens-driveway.jpg";

export default function SeoPage() {
  const params = useParams();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound | error

  const path = location.pathname.replace(/\/+$/, "") || "/";

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setData(null);
    (async () => {
      try {
        const res = await api.get("/seo/content", { params: { path } });
        if (cancelled) return;
        setData(res.data);
        setStatus("ok");
      } catch (e) {
        if (cancelled) return;
        setStatus(e?.response?.status === 404 ? "notfound" : "error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [path]);

  if (status === "loading") return <PageShell loading />;
  if (status === "notfound") return <NotFoundPanel />;
  if (status === "error" || !data) return <NotFoundPanel error />;

  return <SeoPageBody data={data} />;
}

function PageShell({ loading, children }) {
  return (
    <div className="min-h-screen bg-[#FBFAF6]">
      <SeoNav />
      {loading ? (
        <div className="min-h-[60vh] flex items-center justify-center text-slate-500" data-testid="seo-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
        </div>
      ) : (
        children
      )}
    </div>
  );
}

function NotFoundPanel({ error }) {
  return (
    <PageShell>
      <main className="max-w-2xl mx-auto px-5 sm:px-8 lg:px-12 py-20 text-center" data-testid="seo-notfound">
        <AlertCircle className="w-10 h-10 mx-auto text-slate-400 mb-4" />
        <h1 className="font-display text-3xl font-bold text-slate-900">
          {error ? "Something went wrong" : "We don't cover that page yet"}
        </h1>
        <p className="text-slate-600 mt-3">
          {error
            ? "Please try again in a moment."
            : "We're rolling out city pages across the country. In the meantime, get an instant quote and we'll match you with a local screen pro."}
        </p>
        <Link to="/" className="inline-block mt-7">
          <Button size="lg" className="bg-[#0F172A] hover:bg-black rounded-2xl h-12 px-7 text-sm font-bold">
            Get my quote <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </Link>
      </main>
    </PageShell>
  );
}

function SeoPageBody({ data }) {
  const navigate = useNavigate();

  // Reuse the same homeowner quote funnel the homepage uses (PublicEstimate).
  // The "platform" slug is served by the backend as a synthetic config in
  // max-capture mode, with leads landing in `unassigned_leads`.
  const goQuote = () => navigate("/quote");

  return (
    <PageShell>
      <Helmet>
        <title>{data.meta.title}</title>
        <meta name="description" content={data.meta.description} />
        <link rel="canonical" href={data.meta.canonical} />
        <meta property="og:title" content={data.meta.title} />
        <meta property="og:description" content={data.meta.description} />
        <meta property="og:url" content={data.meta.canonical} />
        <meta property="og:type" content="website" />
        {(data.structured_data || []).map((blob, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(blob)}</script>
        ))}
      </Helmet>

      <SeoHero data={data} onQuote={goQuote} />
      <PriceCard data={data} onQuote={goQuote} />
      <IntroBlock data={data} />
      {data.local_concerns && <LocalConcerns data={data} />}
      <RepairVsReplace data={data} />
      <DeepSections data={data} />
      <ServicesGrid data={data} />
      <WhyBlock data={data} onQuote={goQuote} />
      <FAQ data={data} />
      <FinalCTA data={data} onQuote={goQuote} />
      <Footer data={data} />
    </PageShell>
  );
}

/* ----------- Components ----------- */

function SeoNav() {
  return (
    <nav className="px-5 sm:px-8 lg:px-12 py-4 flex items-center justify-between border-b border-slate-200/60 bg-white/80 backdrop-blur sticky top-0 z-40">
      <Link to="/" className="flex items-center gap-2.5">
        <Logo size={36} withWordmark />
      </Link>
      <Link to="/" className="text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="seo-nav-home">Home</Link>
    </nav>
  );
}

function SeoHero({ data, onQuote }) {
  const loc = data.city ? `${data.city.name}, ${data.state.code}` : data.state ? data.state.name : "Your area";
  return (
    <section className="relative">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-12 lg:py-16 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-7 space-y-6">
          <div className="text-xs font-bold uppercase tracking-widest text-[#C75D33]">{loc}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-[1.05]" data-testid="seo-h1">
            {data.h1}
          </h1>
          <p className="text-lg text-slate-600 max-w-xl leading-relaxed">
            {data.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 pt-1">
            <Button
              onClick={onQuote}
              size="lg"
              className="bg-[#0F172A] hover:bg-black rounded-2xl h-14 px-8 text-base font-bold shadow-lg shadow-[#C75D33]/20"
              data-testid="seo-hero-cta"
            >
              Get my instant quote <ArrowRight className="w-5 h-5 ml-1.5" />
            </Button>
            <div className="flex items-center gap-1.5 text-sm text-slate-500 sm:pl-2">
              <ShieldCheck className="w-4 h-4 text-emerald-500" /> Free · 60 seconds · No obligation
            </div>
          </div>
          <ul className="flex flex-wrap gap-2 pt-2">
            <Pill icon={Clock} text="60-second quote" />
            <Pill icon={ShieldCheck} text="Real local pricing" />
            <Pill icon={MapPin} text="Local screen pros" />
          </ul>
        </div>
        <div className="lg:col-span-5">
          <div className="relative rounded-[24px] overflow-hidden shadow-2xl shadow-slate-900/15 aspect-[5/4] bg-slate-200">
            <img src={HERO_IMG} alt="Newly built window screens at a customer's home" className="absolute inset-0 w-full h-full object-cover" loading="eager" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/35 via-transparent to-transparent" />
          </div>
        </div>
      </div>
    </section>
  );
}

function Pill({ icon: Icon, text }) {
  return (
    <li className="inline-flex items-center gap-2 pl-1.5 pr-3.5 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm text-sm font-medium text-slate-700">
      <span className="w-6 h-6 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
        <Icon className="w-3.5 h-3.5" strokeWidth={2.5} />
      </span>
      {text}
    </li>
  );
}

function PriceCard({ data, onQuote }) {
  const where = data.city ? `${data.city.name}` : data.state ? `${data.state.name}` : "your area";
  return (
    <section className="border-y border-slate-200/70 bg-white">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-8 grid sm:grid-cols-3 gap-6 items-center">
        <div className="sm:col-span-2">
          <div className="text-xs font-bold uppercase tracking-widest text-[#C75D33] mb-2">Typical {data.service.short_name.toLowerCase()} pricing in {where}</div>
          <div className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight" data-testid="seo-price-range">
            ${data.price.low}<span className="text-slate-300 mx-2">–</span>${data.price.high}
            <span className="text-base sm:text-lg font-semibold text-slate-500 ml-2">{data.price.unit}</span>
          </div>
          <div className="text-sm text-slate-500 mt-2">Final price depends on screen size, mesh choice, and condition.</div>
        </div>
        <div className="sm:text-right">
          <Button onClick={onQuote} size="lg" className="w-full sm:w-auto bg-[#0F172A] hover:bg-black rounded-2xl h-12 px-6 text-sm font-bold" data-testid="seo-price-cta">
            See exact price <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </div>
    </section>
  );
}

function IntroBlock({ data }) {
  return (
    <section className="py-14 lg:py-16">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 space-y-4">
        {data.intro.map((p, i) => (
          <p key={i} className="text-slate-700 text-base sm:text-lg leading-relaxed">{p}</p>
        ))}
      </div>
    </section>
  );
}

function LocalConcerns({ data }) {
  return (
    <section className="py-12 lg:py-16 bg-white border-y border-slate-200/70">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="max-w-2xl mb-8">
          <div className="text-xs font-bold uppercase tracking-widest text-[#C75D33] mb-3">What we see locally</div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            {data.city ? `Common screen issues in ${data.city.name}` : data.state ? `Common screen issues across ${data.state.name}` : "Common screen issues"}
          </h2>
        </div>
        <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
          {data.local_concerns.map((line, i) => {
            if (line.endsWith(":")) {
              return <li key={i} className="sm:col-span-2 text-xs font-bold uppercase tracking-widest text-slate-500 mt-2 first:mt-0">{line}</li>;
            }
            return (
              <li key={i} className="flex items-start gap-2 text-slate-700">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-1" /> <span>{line}</span>
              </li>
            );
          })}
        </ul>
        {data.neighborhoods && (
          <p className="text-sm text-slate-500 mt-8">
            Serving {data.neighborhoods.slice(0, 3).join(", ")}{data.neighborhoods.length > 3 ? `, and the rest of ${data.city.name}.` : "."}
          </p>
        )}
        {data.hoa_note && <p className="text-sm text-slate-500 mt-2">{data.hoa_note}</p>}
      </div>
    </section>
  );
}

function RepairVsReplace({ data }) {
  if (!data.when_to_use || data.when_to_use.length === 0) return null;
  return (
    <section className="py-14 lg:py-16">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="text-xs font-bold uppercase tracking-widest text-[#C75D33] mb-3">Repair, replace, or upgrade?</div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-5">
          What's the smarter call?
        </h2>
        <ul className="space-y-3">
          {data.when_to_use.map((line, i) => (
            <li key={i} className="flex items-start gap-3 text-slate-700">
              <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0 text-xs font-bold">{i + 1}</span>
              <span className="leading-relaxed">{line}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function DeepSections({ data }) {
  if (!data.sections || data.sections.length === 0) return null;
  return (
    <section className="py-14 lg:py-16 bg-white border-y border-slate-200/70">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 space-y-10">
        {data.sections.map((s, i) => (
          <div key={i}>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-4">{s.heading}</h2>
            <div className="space-y-3">
              {s.body.map((p, j) => (
                <p key={j} className="text-slate-700 text-base sm:text-lg leading-relaxed">{p}</p>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ServicesGrid({ data }) {
  if (!data.available_services || data.available_services.length === 0) return null;
  const locPath = data.city ? `/${data.state.slug}/${data.city.slug}` : data.state ? `/${data.state.slug}` : "";
  return (
    <section className="py-14 lg:py-16">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="max-w-2xl mb-8">
          <div className="text-xs font-bold uppercase tracking-widest text-[#C75D33] mb-3">Other screen services</div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            {data.city
              ? `Screen services in ${data.city.name}`
              : data.state
              ? `Screen services in ${data.state.name}`
              : "Other services"}
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.available_services.map((s) => (
            <Link
              key={s.slug}
              to={`${locPath}/${s.slug}`}
              className="block bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md hover:-translate-y-0.5 transition-all"
              data-testid={`seo-service-link-${s.slug}`}
            >
              <div className="font-display font-bold text-slate-900 text-base">{s.name}</div>
              <div className="text-sm text-slate-600 mt-1 leading-relaxed">{s.summary}</div>
              <div className="text-xs text-[#C75D33] font-semibold mt-3 inline-flex items-center gap-1">
                See pricing <ArrowRight className="w-3 h-3" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyBlock({ data, onQuote }) {
  return (
    <section className="py-14 lg:py-16 bg-white border-y border-slate-200/70">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="max-w-2xl mb-8">
          <div className="text-xs font-bold uppercase tracking-widest text-[#C75D33] mb-3">Why WindowScreenQuote</div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            {data.benefits_lede}
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Why icon={Clock} title="60-second quote" desc="Real numbers in under a minute." />
          <Why icon={ShieldCheck} title="Honest pricing" desc="Based on real local rates." />
          <Why icon={MapPin} title="Local screen pros" desc="Vetted companies in your area." />
          <Why icon={Hammer} title="Done by a real pro" desc="Measured, built, installed." />
        </div>
        <div className="mt-8">
          <Button onClick={onQuote} size="lg" className="bg-[#0F172A] hover:bg-black rounded-2xl h-12 px-7 text-sm font-bold" data-testid="seo-why-cta">
            Get my quote <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </div>
    </section>
  );
}

function Why({ icon: Icon, title, desc }) {
  return (
    <div className="bg-[#FBFAF6] rounded-2xl border border-slate-200 p-5">
      <div className="w-10 h-10 rounded-xl bg-[#C75D33]/10 text-[#C75D33] flex items-center justify-center mb-3"><Icon className="w-5 h-5" /></div>
      <h3 className="font-display font-bold text-slate-900 text-base mb-1">{title}</h3>
      <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
    </div>
  );
}

function FAQ({ data }) {
  if (!data.faqs || data.faqs.length === 0) return null;
  return (
    <section className="py-14 lg:py-16">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="text-xs font-bold uppercase tracking-widest text-[#C75D33] mb-3">FAQ</div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-6">Frequently asked questions</h2>
        <div className="divide-y divide-slate-200 border-y border-slate-200">
          {data.faqs.map((f, i) => (
            <details key={i} className="group py-4" data-testid={`seo-faq-${i}`}>
              <summary className="cursor-pointer font-display font-semibold text-slate-900 text-base sm:text-lg flex items-center justify-between gap-4 list-none">
                <span>{f.q}</span>
                <span className="text-slate-400 group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
              </summary>
              <p className="text-slate-700 mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA({ data, onQuote }) {
  const where = data.city ? data.city.name : data.state ? data.state.name : "your area";
  return (
    <section className="pb-16">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="rounded-[28px] bg-slate-900 px-8 py-12 sm:px-12 sm:py-16 text-center relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#C75D33]/30 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-amber-400/15 blur-3xl" />
          <div className="relative">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">
              See your {data.service.short_name.toLowerCase()} price in {where}.
            </h2>
            <p className="text-slate-300 mt-3 text-base sm:text-lg max-w-xl mx-auto">
              Free. Instant. Real local pricing.
            </p>
            <Button onClick={onQuote} size="lg" className="mt-6 bg-white text-slate-900 hover:bg-slate-100 rounded-2xl h-14 px-8 text-base font-bold" data-testid="seo-final-cta">
              Get my quote <ArrowRight className="w-5 h-5 ml-1.5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer({ data }) {
  const lead = data.city
    ? `${data.city.name}, ${data.state.code}`
    : data.state
    ? `${data.state.name}`
    : "Across the US";
  return (
    <footer className="border-t border-slate-200 bg-white">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-12 lg:py-14 grid grid-cols-2 sm:grid-cols-4 gap-8 sm:gap-6">
        <div className="col-span-2 sm:col-span-1 sm:pr-4">
          <Link to="/" className="flex items-center gap-2.5">
            <Logo size={36} withWordmark />
          </Link>
          <p className="text-sm text-slate-600 mt-3 leading-relaxed">Instant window screen quotes — real local pricing, real local pros.</p>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-900 mb-3">Services</div>
          <ul className="space-y-2 text-sm text-slate-600">
            <li><Link to="/window-screen-repair" className="hover:text-[#C75D33]">Window Screen Repair</Link></li>
            <li><Link to="/new-window-screens" className="hover:text-[#C75D33]">New Window Screens</Link></li>
            <li><Link to="/window-rescreening" className="hover:text-[#C75D33]">Window Rescreening</Link></li>
            <li><Link to="/sliding-screen-door-repair" className="hover:text-[#C75D33]">Sliding Screen Door Repair</Link></li>
            <li><Link to="/retractable-screen-doors" className="hover:text-[#C75D33]">Retractable Screen Doors</Link></li>
            <li><Link to="/security-screen-doors" className="hover:text-[#C75D33]">Security Screen Doors</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-900 mb-3">Popular cities</div>
          <ul className="space-y-2 text-sm text-slate-600">
            <li><Link to="/arizona/phoenix/window-screen-repair" className="hover:text-[#C75D33]">Phoenix, AZ</Link></li>
            <li><Link to="/texas/dallas/window-screen-repair" className="hover:text-[#C75D33]">Dallas, TX</Link></li>
            <li><Link to="/florida/tampa/window-screen-repair" className="hover:text-[#C75D33]">Tampa, FL</Link></li>
            <li><Link to="/california/murrieta/window-screen-repair" className="hover:text-[#C75D33]">Murrieta, CA</Link></li>
            <li><Link to="/nevada/las-vegas/window-screen-repair" className="hover:text-[#C75D33]">Las Vegas, NV</Link></li>
            <li><Link to="/georgia/atlanta/window-screen-repair" className="hover:text-[#C75D33]">Atlanta, GA</Link></li>
            <li><Link to="/north-carolina/charlotte/window-screen-repair" className="hover:text-[#C75D33]">Charlotte, NC</Link></li>
            <li><Link to="/tennessee/nashville/window-screen-repair" className="hover:text-[#C75D33]">Nashville, TN</Link></li>
          </ul>
          <Link to="/locations" className="inline-flex items-center gap-1 text-xs text-[#C75D33] font-semibold mt-3 hover:underline">
            View all locations <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-900 mb-3">Company</div>
          <ul className="space-y-2 text-sm text-slate-600">
            <li><Link to="/services" className="hover:text-[#C75D33]">All services</Link></li>
            <li><Link to="/locations" className="hover:text-[#C75D33]">All locations</Link></li>
            <li><Link to="/terms" className="hover:text-[#C75D33]">Terms</Link></li>
            <li><Link to="/privacy" className="hover:text-[#C75D33]">Privacy</Link></li>
            <li><Link to="/login" className="hover:text-[#C75D33]">Pro login</Link></li>
            <li><Link to="/signup" className="hover:text-[#C75D33]">For screen companies</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-200/70">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-5 flex flex-col sm:flex-row justify-between gap-2 text-xs text-slate-500">
          <span>© WindowScreenQuote.com · {lead}</span>
          <span>Real local pricing from real local screen pros.</span>
        </div>
      </div>
    </footer>
  );
}

